import { useCallback, useEffect, useRef } from 'react';
import {
  getUserPreferences,
  useUserPreferences,
  ZuoraUserPreferences,
} from './useUserPreferences';

/**
 * Navigation settings are persisted under `userPreference.userSettings.lighthouse`.
 * `userSettings` is stored by the backend as a JSON *string*, so every read must
 * defensively parse it and every write must re-serialize it.
 */
const LIGHTHOUSE_KEY = 'lighthouse';

/**
 * Coalesce bursts of writes into a single API request: a write is only flushed
 * after this many milliseconds of quiet.
 */
export const PERSIST_DEBOUNCE_MS = 500;

type LighthouseSettings = Record<string, unknown>;

/** Safely parse the `userSettings` JSON string. Returns `{}` on missing/invalid input. */
const parseUserSettings = (userSettings?: string): Record<string, unknown> => {
  if (!userSettings) {
    return {};
  }
  try {
    const parsed = JSON.parse(userSettings);
    return parsed && typeof parsed === 'object'
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
};

/** Extract the `lighthouse` object from the parsed `userSettings`. */
const getLighthouseSettings = (userSettings?: string): LighthouseSettings => {
  const lighthouse = parseUserSettings(userSettings)[LIGHTHOUSE_KEY];
  return lighthouse && typeof lighthouse === 'object'
    ? (lighthouse as LighthouseSettings)
    : {};
};

const mergeLighthouseSettings = (
  userSettings: string | undefined,
  pendingUpdates: LighthouseSettings,
  removedKeys: Iterable<string>,
): LighthouseSettings => {
  const merged = {
    ...getLighthouseSettings(userSettings),
    ...pendingUpdates,
  };

  for (const key of removedKeys) {
    delete merged[key];
  }

  return merged;
};

const buildUserPreferencesWithLighthouse = (
  preferences: Partial<ZuoraUserPreferences> | undefined,
  lighthouse: LighthouseSettings,
): Partial<ZuoraUserPreferences> => ({
  ...(preferences ?? {}),
  userSettings: JSON.stringify({
    ...parseUserSettings(preferences?.userSettings),
    [LIGHTHOUSE_KEY]: lighthouse,
  }),
});

/**
 * Read/write key-value navigation settings stored inside
 * `userPreference.userSettings.lighthouse`.
 *
 * The returned `getSetting`/`updateSetting`/`removeSetting` callbacks are stable
 * and always operate on the latest preferences via a ref, so they are safe to
 * register once with external consumers (e.g. the ZDS `GlobalApiBridge`).
 */
export const useLighthouseSettings = () => {
  const [
    userPreferences,
    ,
    mutateAsync,
    isUserPreferencesLoading,
    ,
    hasFreshUserPreferences,
  ] = useUserPreferences();

  const userPreferencesRef = useRef<Partial<ZuoraUserPreferences> | undefined>(
    userPreferences,
  );

  // Track only the keys nav changed during the debounce window. Replaying a
  // sparse patch onto the latest server snapshot avoids carrying unrelated
  // lighthouse fields (e.g. homepage-only settings) through a stale overlay.
  const pendingLighthouseUpdatesRef = useRef<LighthouseSettings>({});
  const pendingLighthouseRemovedKeysRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const hasPendingLighthouseChanges =
      Object.keys(pendingLighthouseUpdatesRef.current).length > 0 ||
      pendingLighthouseRemovedKeysRef.current.size > 0;

    if (!hasPendingLighthouseChanges) {
      userPreferencesRef.current = userPreferences;
      return;
    }
    // Take the latest server snapshot and replay only the pending nav key
    // changes. This preserves unrelated lighthouse fields even if another
    // consumer writes or deletes them while nav is still debouncing.
    userPreferencesRef.current = buildUserPreferencesWithLighthouse(
      userPreferences,
      mergeLighthouseSettings(
        userPreferences?.userSettings,
        pendingLighthouseUpdatesRef.current,
        pendingLighthouseRemovedKeysRef.current,
      ),
    );
  }, [userPreferences]);

  // Debounce state: a pending timer plus a single promise shared by every write
  // queued within the current debounce window.
  const flushTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const pendingFlushRef = useRef<{
    promise: Promise<void>;
    resolve: () => void;
    reject: (reason?: unknown) => void;
  } | null>(null);

  // Persist the accumulated nav changes in a single API request. Because
  // `userSettings` is one shared JSON blob written by multiple hosts (homepage,
  // global nav), we must not send a snapshot captured before the debounce
  // window: another host may have written the blob meanwhile. Refetch the
  // freshest server value and replay only the sparse nav deltas onto it, so
  // unrelated lighthouse fields survive.
  const flush = useCallback(async () => {
    flushTimerRef.current = undefined;
    const pending = pendingFlushRef.current;
    pendingFlushRef.current = null;
    if (!pending) {
      return;
    }

    const deltasToFlush = { ...pendingLighthouseUpdatesRef.current };
    const removedKeysToFlush = new Set(pendingLighthouseRemovedKeysRef.current);

    pendingLighthouseUpdatesRef.current = {};
    pendingLighthouseRemovedKeysRef.current.clear();

    try {
      const latest = await getUserPreferences();
      const base = latest ?? {};
      const merged = buildUserPreferencesWithLighthouse(
        base,
        mergeLighthouseSettings(
          base?.userSettings,
          deltasToFlush,
          removedKeysToFlush,
        ),
      );
      userPreferencesRef.current = merged;
      await mutateAsync(merged);

      pending.resolve();
    } catch (error) {
      pendingLighthouseUpdatesRef.current = {
        ...deltasToFlush,
        ...pendingLighthouseUpdatesRef.current,
      };

      for (const key of Array.from(removedKeysToFlush)) {
        if (!(key in pendingLighthouseUpdatesRef.current)) {
          pendingLighthouseRemovedKeysRef.current.add(key);
        }
      }
      pending.reject(error);
    }
  }, [mutateAsync]);

  const scheduleFlush = useCallback(() => {
    if (!pendingFlushRef.current) {
      let resolve!: () => void;
      let reject!: (reason?: unknown) => void;
      const promise = new Promise<void>((res, rej) => {
        resolve = res;
        reject = rej;
      });
      pendingFlushRef.current = { promise, resolve, reject };
    }

    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
    }
    flushTimerRef.current = setTimeout(flush, PERSIST_DEBOUNCE_MS);

    return pendingFlushRef.current.promise;
  }, [flush]);

  // Flush any pending write on unmount so a queued change is never lost.
  useEffect(() => {
    return () => {
      if (flushTimerRef.current) {
        flush().catch((err) => {
          console.warn(
            'LighthouseSettings unmount flush failed silently:',
            err,
          );
        });
      }
    };
  }, [flush]);

  const persistLighthouseSettings = useCallback(
    (nextLighthouse: LighthouseSettings) => {
      // Update the ref synchronously so back-to-back writes accumulate correctly
      // and reads (`getSetting`) reflect the change immediately, even though the
      // API request is debounced.
      userPreferencesRef.current = buildUserPreferencesWithLighthouse(
        userPreferencesRef.current,
        nextLighthouse,
      );

      return scheduleFlush();
    },
    [scheduleFlush],
  );

  const getSetting = useCallback(
    <T = unknown>(key: string, defaultValue: T = null as unknown as T): T => {
      const lighthouse = getLighthouseSettings(
        userPreferencesRef.current?.userSettings,
      );
      return (lighthouse[key] as T) ?? defaultValue;
    },
    [],
  );

  const updateSetting = useCallback(
    async (key: string, value: unknown) => {
      pendingLighthouseRemovedKeysRef.current.delete(key);
      pendingLighthouseUpdatesRef.current = {
        ...pendingLighthouseUpdatesRef.current,
        [key]: value,
      };
      const lighthouse = getLighthouseSettings(
        userPreferencesRef.current?.userSettings,
      );
      await persistLighthouseSettings({ ...lighthouse, [key]: value });
    },
    [persistLighthouseSettings],
  );

  const removeSetting = useCallback(
    async (key: string) => {
      pendingLighthouseRemovedKeysRef.current.add(key);
      const nextPendingUpdates = { ...pendingLighthouseUpdatesRef.current };
      delete nextPendingUpdates[key];
      pendingLighthouseUpdatesRef.current = nextPendingUpdates;
      const lighthouse = {
        ...getLighthouseSettings(userPreferencesRef.current?.userSettings),
      };
      delete lighthouse[key];
      await persistLighthouseSettings(lighthouse);
    },
    [persistLighthouseSettings],
  );

  return {
    userPreferences,
    isUserPreferencesLoading,
    hasFreshUserPreferences,
    getSetting,
    updateSetting,
    removeSetting,
  };
};
